import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatIDR, formatApiError } from '../lib/api';
import { SERVICES, normalizePhone, detectOperator, productOperator, matchesService, validTarget, fetchCatalog, balanceSufficient, hasProductUuid, matchesProductSearch, compareCatalogProducts, productPriceForRole } from '../lib/transaction';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ReceiptPanel from '../components/ReceiptPanel';
import { createIdempotencyKey } from '../lib/idempotency';
export default function TransactionPage() {
  const {
    type
  } = useParams();
  const [params] = useSearchParams();
  const {
    user,
    refreshUser
  } = useAuth();
  const config = SERVICES[type];
  const [number, setNumber] = useState(params.get('prefill') || '');
  const [products, setProducts] = useState([]);
  const [productQuotes, setProductQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState('');
  const [adminFee, setAdminFee] = useState('');
  const request = useRef(null);
  const paymentLock = useRef(false);
  const phone = config?.phone ? normalizePhone(number) : number.trim();
  const operator = config?.phone ? detectOperator(phone) : null;
  const cellular = type === 'pulsa' || type === 'data';
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setProducts([]);
    setProductQuotes({});
    setSelected(null);
    setQuote(null);
    setQuery('');
    setResult(null);
    request.current = null;
    if (!config) {
      setLoading(false);
      return () => controller.abort();
    }
    fetchCatalog(api, config.apiType, controller.signal).then(items => {
      if (!controller.signal.aborted) setProducts(items.filter(p => matchesService(p, type)));
    }).catch(err => {
      if (!controller.signal.aborted) setError(formatApiError(err));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [type, config, reload]);
  useEffect(() => {
    setNumber(params.get('prefill') || '');
  }, [type, params]);
  const visible = useMemo(() => products.filter(p => {
    if (!hasProductUuid(p)) return false;
    const detectedProductOperator = productOperator(p);
    const matchesNumber = !cellular || !operator || !detectedProductOperator || detectedProductOperator === operator;
    return matchesNumber && matchesProductSearch(p, query);
  }).sort(compareCatalogProducts), [products, cellular, operator, query]);
  useEffect(() => {
    const missingProducts = visible.filter(product => productPriceForRole(product) == null && !Object.prototype.hasOwnProperty.call(productQuotes, product.id));
    if (!missingProducts.length) return undefined;
    const controller = new AbortController();
    Promise.all(missingProducts.map(async product => {
      try {
        const { data } = await api.get(`/products/${product.id}/quote`, { signal: controller.signal });
        return [product.id, data];
      } catch (err) {
        if (!controller.signal.aborted) return [product.id, null];
        return null;
      }
    })).then(results => {
      if (controller.signal.aborted) return;
      setProductQuotes(current => ({
        ...current,
        ...Object.fromEntries(results.filter(Boolean))
      }));
    });
    return () => controller.abort();
  }, [visible, productQuotes]);
  useEffect(() => {
    if (selected && !visible.some(p => p.id === selected.id)) setSelected(null);
  }, [visible, selected]);
  useEffect(() => {
    const controller = new AbortController();
    setQuote(null);
    setQuoteError('');
    setQuoteLoading(!!selected);
    if (selected) api.get(`/products/${selected.id}/quote`, {
      signal: controller.signal
    }).then(({ data }) => {
      if (!controller.signal.aborted) setQuote(data);
    }).catch(err => {
      if (!controller.signal.aborted) setQuoteError(formatApiError(err));
    }).finally(() => {
      if (!controller.signal.aborted) setQuoteLoading(false);
    });
    return () => controller.abort();
  }, [selected]);
  const isPending = result && ['pending', 'processing'].includes(String(result.status).toLowerCase());
  useEffect(() => {
    if (!isPending || !result?.id) return;
    const controller = new AbortController();
    const timer = setInterval(() => {
      api.get(`/transactions/${result.id}`, {
        signal: controller.signal
      }).then(({
        data
      }) => {
        if (!controller.signal.aborted) {
          setResult(data);
          if (!['pending', 'processing'].includes(String(data.status).toLowerCase())) refreshUser();
        }
      }).catch(() => {});
    }, 5000);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [isPending, result?.id, refreshUser]);
  const availableBalance = Number(user?.balance ?? user?.wallet?.balance ?? 0);
  const catalogFreshness = products.find(product => product.catalogFreshness)?.catalogFreshness;
  const parsedAdminFee = Number(adminFee || 0);
  const validAdminFee = Number.isInteger(parsedAdminFee) && parsedAdminFee >= 0 && parsedAdminFee <= 100000;
  const totalPayment = Number(quote?.sellPrice ?? 0) + (validAdminFee ? parsedAdminFee : 0);
  const hasEnoughBalance = balanceSufficient(availableBalance, Number(quote?.sellPrice ?? 0));
  const canPay = !!config && validTarget(number, config) && hasProductUuid(selected) && !!quote && quote.product?.id === selected?.id && visible.some(product => product.id === selected?.id) && Number(quote.sellPrice) > 0 && validAdminFee && !quoteLoading && !paying && !result && hasEnoughBalance;
  const onPay = async () => {
    if (!canPay || paymentLock.current) return;
    if (!hasEnoughBalance) {
      toast.error('Saldo Anda tidak cukup untuk transaksi ini. Silakan top up saldo terlebih dahulu.');
      return;
    }
    paymentLock.current = true;
    setPaying(true);
    const signature = `${selected.id}:${phone}:${parsedAdminFee}`;
    if (!request.current || request.current.signature !== signature) {
      const key = createIdempotencyKey();
      request.current = {
        signature,
        key,
        payload: {
          productId: selected.id,
          targetNumber: phone,
          adminFee: parsedAdminFee,
          clientRequestId: key
        }
      };
    }
    try {
      const { data } = await api.post('/transactions', request.current.payload, {
        headers: { 'Idempotency-Key': request.current.key }
      });
      setResult({
        ...data,
        customerAdminFee: Number(data.customerAdminFee ?? data.adminFee ?? parsedAdminFee),
        sellPrice: Number(data.sellPrice ?? data.totalAmount ?? quote.sellPrice) + (data.customerAdminFee != null || data.adminFee != null ? 0 : parsedAdminFee)
      });
      request.current = null;
      refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
      if (!err.response || err.response.status >= 500) toast('Status belum terkonfirmasi. Periksa riwayat atau coba lagi dengan pilihan yang sama.');
    } finally {
      paymentLock.current = false;
      setPaying(false);
    }
  };
  if (!config) return <div className="space-y-4"><h1 className="font-heading text-xl font-bold">Layanan tidak ditemukan</h1><Link to="/transaksi" className="text-brand-green">Kembali ke layanan</Link></div>;
  return <div className="space-y-5 pb-32">
  <header className="flex items-center gap-3"><Button asChild variant="ghost" size="icon" aria-label="Kembali ke layanan"><Link to="/transaksi"><ArrowLeft className="h-5 w-5" /></Link></Button><div><h1 className="font-heading font-bold text-xl">{config.title}</h1><p className="text-xs text-muted-foreground">Pilih tujuan, produk, lalu konfirmasi pembayaran.</p></div></header>
  <section className="rounded-2xl bg-card border border-border p-4 space-y-3"><Label htmlFor="tx-number">{config.label}</Label><Input id="tx-number" data-testid="tx-number-input" value={number} disabled={paying} onChange={e => setNumber(e.target.value)} placeholder={config.phone ? '08… atau +62…' : config.label} inputMode={type === 'game' || type === 'other' || type === 'qris' ? 'text' : 'numeric'} className="h-14 rounded-xl text-lg" />
   {config.phone && <div aria-live="polite" className="text-xs">{operator ? <span data-testid="provider-detected" className="inline-flex rounded-lg bg-brand-green/10 p-2 font-semibold text-brand-green">Terdeteksi: {operator}{cellular ? ' · Produk disesuaikan dengan nomor' : ' · Pilih merek e-wallet tujuan di bawah'}</span> : <span className="text-muted-foreground">{phone.length >= 4 ? 'Operator belum dikenali. Pilih produk yang sesuai dengan nomor tujuan.' : 'Masukkan nomor untuk mendeteksi operator.'}</span>}</div>}
   {number && !validTarget(number, config) && <p className="text-xs text-amber-700">{config.phone ? 'Gunakan nomor HP Indonesia yang valid (10–13 digit).' : 'Gunakan 3–50 karakter: huruf, angka, tanda + atau -.'}</p>}
  </section>
  <section className="space-y-3"><div className="flex justify-between items-center"><h2 className="font-heading font-semibold">Pilih Produk</h2><span className="text-xs text-muted-foreground">{visible.length} di katalog internal</span></div>
   <Input disabled={paying} aria-label="Cari produk" placeholder="Cari nama produk atau merek…" value={query} onChange={e => setQuery(e.target.value)} />
   {loading ? <div role="status" className="p-6 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Memuat katalog…</div> : error ? <div role="alert" className="rounded-xl border border-destructive/30 p-4 text-sm"><p>{error}</p><Button variant="outline" className="mt-3" onClick={() => setReload(v => v + 1)}>Coba lagi</Button></div> : !visible.length ? <div className="rounded-xl bg-muted/40 border border-border p-6 text-center"><h3 className="font-semibold">Belum ada produk tersedia</h3><p className="text-sm text-muted-foreground mt-1">{operator && cellular ? `Katalog ${operator} belum tersedia untuk layanan ini.` : 'Coba pencarian lain atau periksa kembali setelah katalog diperbarui.'}</p></div> : <>
    <p role="note" className="text-xs text-muted-foreground">{catalogFreshness?.state === 'PROVIDER_STATE_UNKNOWN_AFTER_SYNC_FAILURE' ? 'Sinkronisasi provider terakhir gagal. Harga di bawah adalah harga jual internal terakhir yang valid; harga final dan ketersediaan provider tetap harus dikonfirmasi server.' : 'Harga pada katalog adalah harga jual internal. Harga final diperiksa kembali oleh server; katalog bukan jaminan ketersediaan provider.'}</p>
    <div className="grid grid-cols-2 gap-2">{visible.map(p => { const quoteForProduct = productQuotes[p.id]; const catalogPrice = productPriceForRole(p); const quotePrice = Number(quoteForProduct?.sellPrice); const productPrice = catalogPrice ?? (Number.isFinite(quotePrice) && quotePrice > 0 ? quotePrice : null); return <button key={p.id} data-testid={`product-${p.id}`} disabled={paying} aria-pressed={selected?.id === p.id} onClick={() => setSelected(p)} className={`text-left rounded-xl p-3 border-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green ${selected?.id === p.id ? 'border-brand-green bg-brand-green/5' : 'border-border bg-card hover:border-brand-green/40'}`}><div className="text-[10px] font-semibold uppercase tracking-wide text-brand-green">{p.category || config.title}</div><div className="font-heading font-semibold text-sm mt-1 break-words">{p.name}</div><div className="text-sm font-bold mt-2">{productPrice == null ? (Object.prototype.hasOwnProperty.call(productQuotes, p.id) ? 'Harga belum tersedia' : 'Memuat harga…') : formatIDR(productPrice)}</div></button>; })}</div>
   </>}
  </section>
  {quoteLoading && <p role="status" className="text-sm text-muted-foreground">Memeriksa harga terbaru…</p>}
  {quoteError && <div role="alert" className="text-sm text-destructive">{quoteError}<Button variant="ghost" onClick={() => setSelected({
        ...selected
      })}>Coba harga lagi</Button></div>}
  {selected && quote?.product?.id === selected.id && <section data-testid="tx-quote-summary" className="rounded-2xl bg-brand-deep text-white p-5 space-y-3"><h2 className="font-heading font-semibold">Ringkasan Pembayaran</h2><Row k="Produk" v={selected.name} /><Row k="Tujuan" v={phone || 'Belum diisi'} /><div><Label htmlFor="tx-admin-fee" className="text-white">Biaya admin untuk pelanggan</Label><Input id="tx-admin-fee" data-testid="tx-admin-fee-input" value={adminFee} onChange={event => { setAdminFee(event.target.value.replace(/\D/g, '')); request.current = null; }} inputMode="numeric" placeholder="0" disabled={paying} className="mt-1 h-11 border-white/30 bg-white/10 text-white placeholder:text-white/50" />{adminFee && !validAdminFee && <p className="mt-1 text-xs text-rose-200">Biaya admin harus berupa nominal antara Rp0 dan Rp100.000.</p>}</div><Row k="Harga produk" v={formatIDR(quote.sellPrice)} /><Row k="Biaya admin" v={formatIDR(validAdminFee ? parsedAdminFee : 0)} /><div className="border-t border-white/20 pt-3"><Row k="Total Bayar" v={formatIDR(totalPayment)} /></div><p className="text-xs text-white/70">Harga mengikuti katalog backend. Periksa nomor tujuan sebelum membayar.</p></section>}
  <div className="sticky bottom-24"><Button data-testid="tx-pay-btn" onClick={onPay} disabled={!canPay} className="w-full bg-brand-green hover:bg-brand-greenDark rounded-2xl min-h-[60px] text-lg font-semibold text-white">{paying ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Memproses…</> : `Bayar ${quote ? formatIDR(totalPayment) : 'Sekarang'}`}</Button></div>
  <Dialog open={!!result} onOpenChange={open => {
      if (!open) {
        setResult(null);
        setSelected(null);
      }
    }}><DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="tx-result-dialog"><DialogHeader><DialogTitle>Status Transaksi</DialogTitle><DialogDescription>{isPending ? 'Transaksi masih diproses. Status diperbarui otomatis.' : 'Ringkasan hasil transaksi Anda.'}</DialogDescription></DialogHeader>{result && <div className="space-y-4"><ReceiptPanel transaction={{ ...result, product_name: result.product_name || selected?.name, targetNumber: result.targetNumber || result.customer_number || phone }} /><Button asChild variant="outline"><Link to={`/riwayat/${result.id}`}>Detail / Struk</Link></Button><Button className="w-full bg-brand-green" onClick={() => {
            setResult(null);
            setSelected(null);
            setNumber('');
            request.current = null;
          }}><RefreshCw className="h-4 w-4 mr-2" />Transaksi Baru</Button></div>}</DialogContent></Dialog>
 </div>;
}
function Row({
  k,
  v
}) {
  return <div className="flex justify-between gap-4"><span className="opacity-75">{k}</span><span className="font-semibold text-right break-all">{v || '-'}</span></div>;
}
